import { useEffect, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { qualityAPI, NonConformanceReport } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';

interface POLineNCRTabProps {
  lineId: number;
}

const SEVERITY_STYLES: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  major: 'bg-orange-100 text-orange-700',
  minor: 'bg-yellow-100 text-yellow-700',
};

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  ca_submitted: 'bg-blue-100 text-blue-700',
  car_submitted: 'bg-indigo-100 text-indigo-700',
  closed: 'bg-green-100 text-green-700',
  deleted: 'bg-red-100 text-red-500',
};

/**
 * The PO-line modal's NCR tab trigger, shown only when the line has NCRs.
 *
 * NCRs are hidden on this portal except where the site's ERP has recorded a
 * customer reject against the line, so the tab appears only where there is
 * something in it. Counts with a one-row request.
 *
 * @param lineId - PO line id
 * @returns the tab trigger, or null while the line has no NCRs
 */
export function POLineNCRTabTrigger({ lineId }: POLineNCRTabProps) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setCount(0);
    qualityAPI.getNCRs({ po_line: lineId, page_size: 1 })
      .then((res) => { if (!cancelled) setCount(res.data.count); })
      .catch((err: unknown) => {
        // The tab stays hidden rather than showing an empty or broken panel.
        console.error('Failed to count NCRs for line', lineId, err);
      });
    return () => { cancelled = true; };
  }, [lineId]);
  if (count === 0) return null;
  return (
    <TabsTrigger value="ncr" className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary">
      NCRs ({count})
    </TabsTrigger>
  );
}

export default function POLineNCRTab({ lineId }: POLineNCRTabProps) {
  const [ncrs, setNcrs] = useState<NonConformanceReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { formatDate: contextFormatDate } = useFormatters();

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return contextFormatDate(dateString);
  };

  useEffect(() => {
    fetchNCRs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineId]);

  const fetchNCRs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await qualityAPI.getNCRs({ po_line: lineId, page_size: 50 });
      setNcrs(response.data.results || []);
    } catch (err: unknown) {
      console.error('Failed to fetch NCRs:', err);
      setError(getApiErrorMessage(err, 'Failed to load NCRs'));
      toast.error('Failed to load NCRs');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button onClick={fetchNCRs} className="mt-2 text-sm text-red-600 hover:text-red-800 underline">
          Try again
        </button>
      </div>
    );
  }

  if (ncrs.length === 0) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">No NCRs found for this PO line</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex items-center gap-4">
        <div className="text-sm text-muted-foreground">
          {ncrs.length} NCR{ncrs.length !== 1 ? 's' : ''} found
        </div>
        {ncrs.some((n) => n.erp_reject_id) && (
          <div className="text-xs text-muted-foreground">Customer rejects are recorded in the ERP and are read-only here.</div>
        )}
      </div>

      {/* NCR Table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 border-b">
              <th className="text-left px-4 py-3 font-medium">NCR Number</th>
              <th className="text-left px-4 py-3 font-medium">Customer Ref</th>
              <th className="text-left px-4 py-3 font-medium">Date</th>
              <th className="text-left px-4 py-3 font-medium">Part Number</th>
              <th className="text-left px-4 py-3 font-medium">Severity</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Reject Qty</th>
              <th className="text-left px-4 py-3 font-medium">Raised By</th>
            </tr>
          </thead>
          <tbody>
            {ncrs.map((ncr) => (
              <tr key={ncr.id} className="border-b last:border-b-0 hover:bg-muted/30">
                <td className="px-4 py-3 font-medium text-primary">
                  {ncr.ncr_number}
                  {ncr.erp_reject_id ? <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-semibold">ERP</span> : null}
                </td>
                <td className="px-4 py-3">{ncr.customer_ncr_number || '-'}</td>
                <td className="px-4 py-3">{formatDate(ncr.ncr_date)}</td>
                <td className="px-4 py-3">
                  {ncr.part_number}
                  {ncr.defect_description && (
                    <p className="text-xs text-muted-foreground mt-1 max-w-md whitespace-pre-line">{ncr.defect_description}</p>
                  )}
                </td>
                <td className="px-4 py-3">
                  {ncr.severity ? (
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold capitalize ${SEVERITY_STYLES[ncr.severity] || 'bg-gray-100 text-gray-700'}`}>
                      {ncr.severity_display || ncr.severity}
                    </span>
                  ) : '-'}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${STATUS_STYLES[ncr.status] || 'bg-gray-100 text-gray-700'}`}>
                    {ncr.status_display || ncr.status}
                  </span>
                </td>
                <td className="px-4 py-3">{ncr.reject_qty}</td>
                <td className="px-4 py-3">{ncr.erp_reject_id ? 'ERP' : ncr.raised_by_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Defect Summary */}
      {ncrs.length > 0 && (
        <div className="bg-muted/30 rounded-lg p-4">
          <h4 className="text-sm font-medium mb-2">Summary</h4>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Total NCRs</span>
              <p className="font-semibold">{ncrs.length}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Total Reject Qty</span>
              <p className="font-semibold">{ncrs.reduce((sum, n) => sum + (Number(n.reject_qty) || 0), 0)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Open</span>
              <p className="font-semibold">{ncrs.filter((n) => n.status !== 'closed' && n.status !== 'deleted').length}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Closed</span>
              <p className="font-semibold">{ncrs.filter((n) => n.status === 'closed').length}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
